import { useState, useEffect } from 'react';
import { useMemo } from 'react';
import iconClose from '../../../assets/images/icon-close2.svg';
import iconCheck from '../../../assets/images/icon-check-read.svg';
import { NotificationsHolder } from './Notifications.styles';
import dashboardService from '../../../services/dashboardService';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../../Context/authContext';
import InfiniteScroll from 'react-infinite-scroll-component';
import { StyledDotsLoader } from '../../atoms/Unibox/Unibox.styles';
import Loaders from '../../atoms/Loaders';
import Toast from '../Toast';

const Notifications = ({ notification_card, setNotifications }) => {
  const { fetch } = useContextHook(AuthContext, v => ({
    fetch: v.fetch,
  }));
  const [searchQuery, setSearchQuery] = useState({
    page: 1,
    itemsPerPage: 5,
  });
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);
  const [notificationsData, setNotificationsData] = useState(null);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState(false);
  const [visibleItems, setVisibleItems] = useState([]);
  const [hasMore, setHasMore] = useState(true);

  // Make API call when component is first rendered
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setNotificationsLoading(true);
        setNotificationsError(false);
        const response = await dashboardService.getNotifications(searchQuery);
        setNotificationsData(response);
      } catch (error) {
        console.error('Error fetching notifications:', error);
        setNotificationsError(true);
      } finally {
        setNotificationsLoading(false);
      }
    };

    if (notification_card) {
      fetchNotifications();
    }
  }, [notification_card, searchQuery.page]); // Empty dependency array - only runs once when component mounts

  const items = useMemo(
    () => (Array.isArray(notificationsData?.data?.items) ? notificationsData.data.items : []),
    [notificationsData],
  );

  const unreadCount = visibleItems.filter(i => !i?.isRead).length;

  useEffect(() => {
    if (items.length > 0) {
      if ((searchQuery?.page || 1) === 1) {
        setVisibleItems(items);
      } else {
        setVisibleItems(prev => {
          const existingIds = new Set(prev.map(item => item.id));
          const newItems = items.filter(item => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      }
      setHasMore(notificationsData?.data?.hasNextPage || false);
    }
  }, [items, notificationsData, searchQuery.page]);

  const fetchMoreData = () => {
    if (!notificationsData?.data?.hasNextPage) {
      setHasMore(false);
      return;
    }

    setSearchQuery(prev => ({
      ...prev,
      page: notificationsData?.data?.nextPage || prev.page + 1,
    }));
  };

  useEffect(() => {
    if (items.length > 0) {
      if ((searchQuery?.page || 1) === 1) {
        setVisibleItems(items);
      } else {
        setVisibleItems(prev => {
          const existingIds = new Set(prev.map(item => item.id));
          const newItems = items.filter(item => !existingIds.has(item.id));
          return [...prev, ...newItems];
        });
      }
      setHasMore(notificationsData?.data?.hasNextPage || false);
    }
  }, [items, notificationsData, searchQuery.page]);

  const handleMarkAllAsRead = async () => {
    try {
      setMarkAsReadLoading(true);
      let res = await dashboardService.markAsread();

      if (res.success) {
        setSearchQuery({
          page: 1,
          itemsPerPage: 5,
        });
        setVisibleItems([]);
        setHasMore(true);
        // Refresh notifications after marking as read
        const response = await dashboardService.getNotifications({ page: 1, itemsPerPage: 5 });
        setNotificationsData(response);
      }
    } catch (error) {
      Toast({ type: 'error', message: error.message || 'Failed to mark notifications as read' });
    } finally {
      setMarkAsReadLoading(false);
    }
  };

  return (
    <NotificationsHolder>
      <div className="holder">
        <div className="head">
          <span className="heading">Notification</span>
          <button className="btn-close" onClick={() => setNotifications(false)}>
            <img src={iconClose} width="25" alt="close" />
          </button>
        </div>
        <div className="scroll-area" id="notifications-scrollable">
          {notificationsLoading && visibleItems.length === 0 ? (
            <Loaders dotsLoader />
          ) : notificationsError ? (
            <div className="content-box">
              <div className="text-box">
                <p>Error loading notifications. Please try again.</p>
              </div>
            </div>
          ) : visibleItems.length === 0 ? (
            <div className="content-box">
              <div className="text-box">
                <p>No notifications found.</p>
              </div>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={visibleItems.length}
              next={fetchMoreData}
              hasMore={hasMore}
              scrollableTarget="notifications-scrollable"
              loader={
                <StyledDotsLoader $loaderBottom>
                  <div>
                    <div className="dots-loader" />
                  </div>
                </StyledDotsLoader>
              }>
              {visibleItems.map((n, index) => {
                const title = n?.title || 'Notification';
                const message = n?.message || '';
                const isNew = n?.isRead;
                return (
                  <div className="content-box" key={n?.id || index}>
                    <div className="title-header">
                      <span className="title">{title}</span>
                      {!isNew && <span className="tag">New</span>}
                    </div>
                    {message && (
                      <div className="text-box">
                        <p>{message}</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </InfiniteScroll>
          )}
        </div>
        {visibleItems.length > 0 && (
          <button
            disabled={unreadCount === 0 || markAsReadLoading}
            className={`mark-read ${markAsReadLoading ? 'loading' : unreadCount === 0 ? 'all-read' : ''}`}
            onClick={handleMarkAllAsRead}>
            <img src={iconCheck} width="20" alt="close" />
            {markAsReadLoading ? 'Marking as read...' : 'Mark all as read'}
          </button>
        )}
      </div>
    </NotificationsHolder>
  );
};

export default Notifications;
